import DOMPurify from "dompurify";
import { SearchResult } from "components/Molecules/SearchCard/SearchCardModel";
import { newsContentText, newsSearchPattern, normalizeNewsSearch } from "utils/newsSearch";
import { SearchRecord, SearchSource, SiteSearchResponse } from "./siteSearchModel";
import { siteSearchSources } from "./siteSearchSources";

const readableText = (value: unknown): string => {
  const safeText = DOMPurify.sanitize(newsContentText(value), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  const decoder = document.createElement("textarea");
  decoder.innerHTML = safeText;
  return decoder.value.replace(/\s+/g, " ").trim();
};

export const mapSearchRecord = (record: SearchRecord, source: SearchSource, query: string): SearchResult | null => {
  const title = readableText(record.attributes.title);
  if (!title) return null;
  const text = source.fields.filter((field) => field !== "title")
    .map((field) => readableText(record.attributes[field])).filter(Boolean).join(" · ");
  const normalized = normalizeNewsSearch(query);
  if (![title, text].some((field) => normalizeNewsSearch(field).includes(normalized))) return null;
  const matchPosition = normalizeNewsSearch(text).indexOf(normalized);
  const start = Math.max(0, matchPosition - 70);
  const excerpt = text.slice(start, start + 260);
  const url = source.destination(record);
  return {
    id: `${source.endpoint}-${record.id}`, title, url,
    content: `${start ? "…" : ""}${excerpt}${text.length > start + 260 ? "…" : ""}`,
    categoryKey: source.categoryKey, external: /^https?:\/\//.test(url),
  };
};

export class SiteSearchService {
  private cache = new Map<string, { results: SearchResult[]; expiresAt: number }>();

  constructor(private sources: SearchSource[] = siteSearchSources) {}

  private async searchSource(source: SearchSource, query: string, locale: string, signal?: AbortSignal): Promise<SearchResult[]> {
    const params = new URLSearchParams({ "pagination[pageSize]": "100", "sort": "id:desc", ...source.populate });
    if (source.localized) params.set("locale", locale);
    [...source.fields, ...(source.extraFields ?? [])].forEach((field, index) => params.set(`fields[${index}]`, field));
    source.fields.forEach((field, index) => params.set(`filters[$or][${index}][${field}][$containsi]`, newsSearchPattern(query)));
    const results: SearchResult[] = [];
    let page = 1;
    let pageCount = 1;
    do {
      params.set("pagination[page]", String(page));
      const { data } = await source.client.get<{ data: SearchRecord[]; meta: { pagination: { pageCount: number } } }>(
        `${source.endpoint}?${params}`, { signal }
      );
      pageCount = data.meta.pagination.pageCount;
      data.data.forEach((record) => {
        const result = mapSearchRecord(record, source, query);
        if (result) results.push(result);
      });
      page += 1;
    } while (page <= pageCount);
    return results;
  }

  async search(query: string, locale = "ro", signal?: AbortSignal): Promise<SiteSearchResponse> {
    const normalized = normalizeNewsSearch(query);
    if (!normalized) return { results: [], incomplete: false };
    const key = JSON.stringify([normalized, locale]);
    const cached = this.cache.get(key);
    if (cached && cached.expiresAt > Date.now()) return { results: cached.results, incomplete: false };
    const responses = await Promise.allSettled(this.sources.map((source) => this.searchSource(source, query, locale, signal)));
    if (signal?.aborted) throw new DOMException("Search cancelled", "AbortError");
    const successful = responses.filter((response): response is PromiseFulfilledResult<SearchResult[]> => response.status === "fulfilled");
    if (!successful.length) throw new Error("Search sources unavailable");
    const results = successful.flatMap((response) => response.value);
    const titleScore = (result: SearchResult) => {
      const title = normalizeNewsSearch(result.title);
      return title === normalized ? 3 : title.startsWith(normalized) ? 2 : title.includes(normalized) ? 1 : 0;
    };
    results.sort((left, right) => titleScore(right) - titleScore(left));
    const incomplete = successful.length < responses.length;
    if (!incomplete) {
      if (this.cache.size >= 10) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(key, { results, expiresAt: Date.now() + 60_000 });
    }
    return { results, incomplete };
  }
}

const siteSearchService = new SiteSearchService();
export default siteSearchService;
